import React, { useCallback, useEffect, useState } from 'react';
import {
  XMarkIcon,
  CheckCircleIcon,
  ClockIcon,
  ArrowPathIcon,
  ChatBubbleLeftRightIcon,
  DocumentIcon,
} from '@heroicons/react/24/outline';
import { api } from '../services/api';

interface ThreadComment {
  id: number;
  body: string;
  is_from_admin: boolean;
  created_at: string | null;
  author: { username: string } | null;
}

interface MyFeedbackItem {
  id: number;
  created_at: string | null;
  rating: string | null;
  subject: string | null;
  comment: string | null;
  solved: boolean;
  solved_at: string | null;
  solved_note: string | null;
  solved_by: { username: string } | null;
  invoice: { id: number; filename: string | null } | null;
  comments: ThreadComment[];
}

interface Props {
  onClose: () => void;
}

type StatusTab = 'open' | 'solved' | 'all';

const fmtDate = (iso: string | null) => {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  } catch {
    return iso;
  }
};

const fmtDateTime = (iso: string | null) => {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    const date = d.toLocaleDateString(undefined, {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
    const time = d.toLocaleTimeString(undefined, {
      hour: 'numeric',
      minute: '2-digit',
    });
    return `${date} at ${time}`;
  } catch {
    return iso;
  }
};

const MyFeedbackPanel: React.FC<Props> = ({ onClose }) => {
  const [items, setItems] = useState<MyFeedbackItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [tab, setTab] = useState<StatusTab>('open');
  const [q, setQ] = useState('');
  const [replyingFor, setReplyingFor] = useState<number | null>(null);
  const [replyText, setReplyText] = useState('');
  const [replySubmitting, setReplySubmitting] = useState(false);
  const [expandedThreads, setExpandedThreads] = useState<Set<number>>(new Set());
  const toggleThread = (fbId: number) => {
    setExpandedThreads((prev) => {
      const next = new Set(prev);
      if (next.has(fbId)) next.delete(fbId);
      else next.add(fbId);
      return next;
    });
  };

  const submitReply = async (fbId: number) => {
    const body = replyText.trim();
    if (!body) return;
    setReplySubmitting(true);
    try {
      await api.post(`/feedback/mine/${fbId}/reply`, { body });
      setReplyingFor(null);
      setReplyText('');
      await load();
    } catch (e: any) {
      setError(e?.response?.data?.detail || e?.message || 'Failed to send reply');
    } finally {
      setReplySubmitting(false);
    }
  };

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.get('/feedback/mine');
      setItems(res.data?.items || []);
    } catch (e: any) {
      setError(e?.response?.data?.detail || e?.message || 'Failed to load your feedback');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const solvedCount = items.filter((i) => i.solved).length;
  const openCount = items.length - solvedCount;

  const needle = q.trim().toLowerCase();
  const visible = items
    .filter((fb) => {
      if (tab === 'open' && fb.solved) return false;
      if (tab === 'solved' && !fb.solved) return false;
      if (needle) {
        const commentText = (fb.comments || []).map((c) => c.body).join(' ');
        const hay = `${fb.subject || ''} ${fb.comment || ''} ${fb.solved_note || ''} ${commentText}`.toLowerCase();
        if (!hay.includes(needle)) return false;
      }
      return true;
    })
    .sort((a, b) => {
      const ta = a.created_at ? new Date(a.created_at).getTime() : 0;
      const tb = b.created_at ? new Date(b.created_at).getTime() : 0;
      return tb - ta;
    });

  const tabBtn = (key: StatusTab, label: string, count: number) => {
    const active = tab === key;
    return (
      <button
        key={key}
        type="button"
        onClick={() => setTab(key)}
        className={`px-3 py-1.5 text-xs font-medium rounded-md border transition-colors ${
          active
            ? 'bg-brand-600 border-brand-600 text-white'
            : 'bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700'
        }`}
      >
        {label}
        <span
          className={`ml-1.5 text-[10px] px-1.5 py-0.5 rounded-full ${
            active
              ? 'bg-white/20 text-white'
              : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300'
          }`}
        >
          {count}
        </span>
      </button>
    );
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl max-w-3xl w-full max-h-[85vh] flex flex-col">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-2">
            <ChatBubbleLeftRightIcon className="h-5 w-5 text-brand-600 dark:text-brand-400" />
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">My Feedback</h2>
            {items.length > 0 && (
              <span className="text-xs text-gray-500 dark:text-gray-400 ml-2">
                {openCount} open, {solvedCount} solved
              </span>
            )}
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={load}
              disabled={loading}
              className="inline-flex items-center gap-1 px-2 py-1 text-xs text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-md disabled:opacity-50"
              title="Refresh"
            >
              <ArrowPathIcon className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            </button>
            <button
              type="button"
              onClick={onClose}
              className="p-1 text-gray-500 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-700 rounded-md"
              aria-label="Close"
            >
              <XMarkIcon className="h-5 w-5" />
            </button>
          </div>
        </div>

        {items.length > 0 && (
          <div className="px-5 py-3 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/40 flex flex-wrap items-center gap-2">
            {tabBtn('open', 'Open', openCount)}
            {tabBtn('solved', 'Solved', solvedCount)}
            {tabBtn('all', 'All', items.length)}
            <input
              type="text"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search..."
              className="ml-auto flex-1 min-w-[140px] max-w-[240px] px-2 py-1 text-xs border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
            />
          </div>
        )}

        <div className="flex-1 overflow-y-auto p-5">
          {error && (
            <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-sm text-red-700 dark:text-red-300">
              {error}
            </div>
          )}

          {loading && items.length === 0 && (
            <div className="text-center text-sm text-gray-500 dark:text-gray-400 py-8">
              Loading your feedback...
            </div>
          )}

          {!loading && items.length === 0 && !error && (
            <div className="text-center py-10">
              <ChatBubbleLeftRightIcon className="h-10 w-10 text-gray-300 dark:text-gray-600 mx-auto mb-2" />
              <p className="text-sm text-gray-600 dark:text-gray-400">
                You haven&apos;t submitted any feedback yet.
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-500 mt-1">
                Use Send Feedback to share anything, good or bad.
              </p>
            </div>
          )}

          {!loading && items.length > 0 && visible.length === 0 && (
            <div className="text-center py-10 text-sm text-gray-500 dark:text-gray-400">
              {tab === 'open'
                ? 'No open feedback. Nice, all caught up.'
                : tab === 'solved'
                  ? 'No solved feedback yet.'
                  : 'Nothing matches your search.'}
            </div>
          )}

          {items.length >= 200 && (
            <div className="mb-3 p-2 text-[11px] text-gray-500 dark:text-gray-400 bg-gray-100 dark:bg-gray-900/40 border border-gray-200 dark:border-gray-700 rounded-md text-center">
              Showing the latest 200 items. Older feedback is retained but not listed here.
            </div>
          )}

          <div className="space-y-3">
            {visible.map((fb) => (
              <div
                key={fb.id}
                className="border border-gray-200 dark:border-gray-700 rounded-lg p-3 bg-gray-50 dark:bg-gray-800/60"
              >
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div className="flex-1 min-w-[220px]">
                    <div className="flex items-center gap-2 mb-1">
                      {fb.solved ? (
                        <span className="inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-full bg-emerald-100 dark:bg-emerald-900/40 text-emerald-800 dark:text-emerald-300">
                          <CheckCircleIcon className="h-3 w-3" />
                          Solved
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-full bg-amber-100 dark:bg-amber-900/40 text-amber-800 dark:text-amber-300">
                          <ClockIcon className="h-3 w-3" />
                          Open
                        </span>
                      )}
                      <span className="text-[11px] text-gray-500 dark:text-gray-400">
                        {fmtDate(fb.created_at)}
                      </span>
                    </div>
                    <div className="text-sm font-medium text-gray-900 dark:text-white">
                      {fb.subject || <em className="text-gray-500">(no subject)</em>}
                    </div>
                    {fb.comment && (
                      <div className="text-xs text-gray-700 dark:text-gray-300 mt-1 whitespace-pre-wrap break-words">
                        {fb.comment}
                      </div>
                    )}
                    {fb.invoice && (
                      <div className="text-[11px] text-gray-500 dark:text-gray-400 mt-1 flex items-center gap-1">
                        <DocumentIcon className="h-3 w-3" />
                        invoice #{fb.invoice.id}
                        {fb.invoice.filename ? ` (${fb.invoice.filename})` : ''}
                      </div>
                    )}
                    {fb.comments && fb.comments.length > 0 && (
                      <button
                        type="button"
                        onClick={() => toggleThread(fb.id)}
                        className="mt-1 inline-flex items-center gap-1 text-[11px] text-brand-700 dark:text-brand-300 hover:underline"
                      >
                        <ChatBubbleLeftRightIcon className="h-3 w-3" />
                        {expandedThreads.has(fb.id) ? 'Hide' : 'Show'} {fb.comments.length} repl{fb.comments.length === 1 ? 'y' : 'ies'}
                      </button>
                    )}
                  </div>
                </div>

                {fb.comments && fb.comments.length > 0 && expandedThreads.has(fb.id) && (
                  <div className="mt-2 space-y-2">
                    {fb.comments.map((c) => (
                      <div
                        key={c.id}
                        className={`pl-3 border-l-2 rounded-r p-2 ${
                          c.is_from_admin
                            ? 'border-brand-400 dark:border-brand-600 bg-brand-50/60 dark:bg-brand-900/10'
                            : 'border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800/60'
                        }`}
                      >
                        <div
                          className={`text-[11px] font-semibold ${
                            c.is_from_admin
                              ? 'text-brand-800 dark:text-brand-300'
                              : 'text-gray-700 dark:text-gray-300'
                          }`}
                        >
                          {c.is_from_admin ? 'KeyScan support' : 'You replied'}
                          {c.created_at ? ` on ${fmtDateTime(c.created_at)}` : ''}
                        </div>
                        <div className="text-xs text-gray-800 dark:text-gray-200 mt-1 whitespace-pre-wrap break-words">
                          {c.body}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                {fb.solved && (!fb.comments || fb.comments.length === 0) && (
                  <div className="mt-2 text-[11px] text-gray-500 dark:text-gray-400 italic pl-3">
                    Marked solved{fb.solved_by ? ` by ${fb.solved_by.username}` : ''} with no reply.
                  </div>
                )}

                {!fb.solved && (
                  <div className="mt-2">
                    {replyingFor === fb.id ? (
                      <div className="p-2 border border-brand-300 dark:border-brand-700 bg-brand-50/40 dark:bg-brand-900/20 rounded-md">
                        <textarea
                          value={replyText}
                          onChange={(e) => setReplyText(e.target.value.slice(0, 2000))}
                          rows={3}
                          maxLength={2000}
                          placeholder="Reply here so support can pick this back up."
                          className="w-full px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
                          autoFocus
                        />
                        <div className="text-[10px] text-gray-500 dark:text-gray-400 text-right mt-0.5">
                          {replyText.length}/2000
                        </div>
                        <div className="flex justify-end gap-2 mt-1">
                          <button
                            type="button"
                            onClick={() => {
                              setReplyingFor(null);
                              setReplyText('');
                            }}
                            disabled={replySubmitting}
                            className="px-3 py-1 text-xs rounded-md border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50"
                          >
                            Cancel
                          </button>
                          <button
                            type="button"
                            onClick={() => submitReply(fb.id)}
                            disabled={replySubmitting || !replyText.trim()}
                            className="px-3 py-1 text-xs rounded-md bg-brand-600 hover:bg-brand-700 text-white disabled:opacity-50"
                          >
                            {replySubmitting ? 'Sending...' : 'Send reply'}
                          </button>
                        </div>
                      </div>
                    ) : (
                      <button
                        type="button"
                        onClick={() => {
                          setReplyingFor(fb.id);
                          setReplyText('');
                          if (fb.comments && fb.comments.length > 0) {
                            setExpandedThreads((prev) => new Set(prev).add(fb.id));
                          }
                        }}
                        className="inline-flex items-center gap-1 px-2 py-1 text-xs rounded-md border border-brand-300 dark:border-brand-700 text-brand-700 dark:text-brand-300 hover:bg-brand-50 dark:hover:bg-brand-900/20"
                      >
                        <ChatBubbleLeftRightIcon className="h-3.5 w-3.5" />
                        {fb.comments && fb.comments.some((c) => c.is_from_admin) ? 'Reply back' : 'Add a reply'}
                      </button>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default MyFeedbackPanel;
